/**
 * موقعیتِ کاربر روی مرورگر — فقط برای ترتیبِ «نزدیک‌ترین».
 *
 * قرینه‌ی apps/mobile/src/features/discover/useMyLocation.ts و همون دو تصمیم:
 *
 * ۱) اجازه **تنبل** گرفته می‌شه، نه موقعِ باز شدنِ صفحه. تا وقتی کسی ترتیبِ
 *    فاصله رو انتخاب نکرده، دلیلی برای بالا آوردنِ پنجره‌ی اجازه‌ی مرورگر
 *    نیست — و پرسیدنِ بی‌دلیل همون چیزیه که آدم‌ها رد می‌کنن.
 *
 * ۲) دقتِ پایین کافیه (`enableHighAccuracy: false`). برای «کدوم نزدیک‌تره»
 *    چند صد متر خطا هیچ ترتیبی رو عوض نمی‌کنه.
 *
 * چرا توی core نرفت با اینکه اسم و حالت‌هاش عینِ موبایله: از این هوک هیچ
 * چیزی مشترک نیست جز همین دو تا نوعِ کوچیک. تنه‌ش سرتاسر پیاده‌سازیِ پلتفرمه
 * — اون‌جا `expo-location` با اجازه‌ی سیستمی و «آخرین موقعیتِ شناخته‌شده»،
 * این‌جا `navigator.geolocation` با اجازه‌ی به‌ازای سایت و یه callback. بردنش
 * به core یعنی یه پورتِ تازه توی ports.ts برای چیزی که هر طرف بیست خط است.
 *
 * نتیجه توی حافظه‌ی همین صفحه می‌مونه: عوض کردنِ چند باره‌ی ترتیب نباید هر
 * بار یه درخواستِ تازه‌ی GPS بزنه.
 */
import { useCallback, useRef, useState } from 'react';

export interface Coords { lat: number; lng: number }

export type LocationState = 'idle' | 'asking' | 'ready' | 'denied' | 'failed';

/** بیشتر از این منتظرِ مرورگر نمی‌مونیم؛ فهرست نباید تا ابد بی‌ترتیب بمونه. */
const TIMEOUT_MS = 10_000;
/** موقعیتِ تا پنج دقیقه پیش کافیه — «کدوم نزدیک‌تره» با پیاده‌روی عوض نمی‌شه. */
const MAX_AGE_MS = 5 * 60_000;

export function useMyLocation() {
  const [coords, setCoords] = useState<Coords | null>(null);
  const [state, setState] = useState<LocationState>('idle');
  // دو بار پشتِ سرِ هم زدنِ «نزدیک‌ترین» نباید دو تا پنجره‌ی اجازه باز کنه.
  const asking = useRef(false);

  /**
   * موقعیت رو بگیر، و اگه لازم بود اجازه بپرس.
   *
   * خودِ مختصات رو هم برمی‌گردونه، نه فقط `state` رو عوض می‌کنه: صداکننده‌ای
   * که می‌خواد بلافاصله کاری با مختصات بکنه (مثلِ «شهرم رو پیدا کن» توی
   * فرمِ ساختِ نمایه) نباید مجبور شه یه useEffect بذاره و منتظرِ رندرِ بعدی
   * بمونه. قرینه‌ی موبایل که از اول همین‌طور بود.
   */
  const ensure = useCallback((): Promise<Coords | null> => {
    if (coords) return Promise.resolve(coords);
    if (asking.current) return Promise.resolve(null);
    // مرورگرِ خیلی قدیمی یا context ناامن (http بدونِ localhost): این API
    // اصلاً وجود نداره. از دیدِ کاربر همون «نشد» است.
    if (!navigator.geolocation) { setState('failed'); return Promise.resolve(null); }

    asking.current = true;
    setState('asking');
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          asking.current = false;
          const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(next);
          setState('ready');
          resolve(next);
        },
        (err) => {
          asking.current = false;
          // فقط `PERMISSION_DENIED` یعنی کاربر «نه» گفته — پیامِ «از تنظیمات
          // روشنش کن» برای بقیه (تایم‌اوت، موقعیتِ در دسترس نیست) دروغه.
          setState(err.code === err.PERMISSION_DENIED ? 'denied' : 'failed');
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: TIMEOUT_MS, maximumAge: MAX_AGE_MS },
      );
    });
  }, [coords]);

  return { coords, state, ensure };
}
